import { Children } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import FaqAccordion, { groupFaqByCategory } from "../../components/shared/FaqAccordion.jsx";
import Breadcrumbs from "../../components/shared/Breadcrumbs.jsx";
import ShareLinks from "../../components/shared/ShareLinks.jsx";
import ResponsiveImage from "../../components/shared/ResponsiveImage.jsx";
import NewsletterForm from "../../components/newsletter/NewsletterForm.jsx";
import { larpPages, getLarpPage, findByLangAndId, estimateReadingTime, getReadableText, extractHeadings, slugify, excerpt, formatDate } from "../../utils/content.js";
import { buildFaqJsonLd, buildDefinedTermSetJsonLd, buildArticleJsonLd, buildBreadcrumbJsonLd } from "../../utils/jsonld.js";
import { AUTHORS } from "../../data/authors.js";
import NotFound from "../NotFound.jsx";

const LARP_PATH = { de: "larp-erleben", en: "experience-larp" };

function headingText(children) {
  return Children.toArray(children)
    .map((child) => (typeof child === "string" ? child : ""))
    .join("");
}

function ArticleH2({ children }) {
  const id = slugify(headingText(children));
  return (
    <h2 id={id} style={{ fontFamily: "var(--font-serif)", fontSize: "26px", lineHeight: 1.25, color: "var(--color-cream)", marginTop: "12px" }}>
      {children}
    </h2>
  );
}

function ArticleImage({ src, alt }) {
  return (
    <ResponsiveImage
      src={src}
      alt={alt}
      variant="dark"
      style={{ width: "100%", height: "260px", borderRadius: "12px", objectFit: "cover", display: "block", margin: "8px 0" }}
    />
  );
}

function ArticleBlockquote({ children }) {
  return (
    <blockquote style={{ borderLeft: "3px solid var(--color-gold)", padding: "4px 0 4px 24px", margin: "16px 0", fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "20px", lineHeight: 1.6, color: "var(--color-cream)" }}>
      {children}
    </blockquote>
  );
}

/** "Weiterlesen" suggestions come straight from the article's own
 * `related` frontmatter (an array of other articles' `id`s) — no separate
 * curation file, so the relation lives next to the content it's about. */
function useRelatedPages(lang, page) {
  const relatedIds = page.frontmatter.related ?? [];
  return relatedIds
    .map((id) => findByLangAndId(larpPages, lang, id))
    .filter(Boolean)
    .slice(0, 3);
}

export default function LarpSubpage({ lang }) {
  const { slug } = useParams();
  const { t } = useTranslation();
  const page = getLarpPage(lang, slug);

  if (!page) return <NotFound lang={lang} />;

  const { frontmatter, content } = page;
  const otherLang = lang === "de" ? "en" : "de";
  // The DE/EN sibling is whichever other-language page shares this one's
  // `id` frontmatter — not a lookup table, so DE and EN slugs are free to
  // differ (or not) per article.
  const sibling = findByLangAndId(larpPages, otherLang, frontmatter.id);
  const path = `/${LARP_PATH[lang]}/${slug}`;
  const altPath = sibling ? `/${LARP_PATH[otherLang]}/${sibling.frontmatter.slug}` : path;
  const relatedPages = useRelatedPages(lang, page);
  const headings = extractHeadings(content);
  const breadcrumbItems = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.larpErleben"), path: `/${LARP_PATH[lang]}` },
    { name: frontmatter.title, path },
  ];

  return (
    <>
      <SEOHead
        lang={lang}
        path={path}
        altPath={altPath}
        hasTranslation={Boolean(sibling)}
        title={frontmatter.metaTitle || frontmatter.title}
        category={frontmatter.metaTitle ? undefined : t("larp.category")}
        description={frontmatter.metaDescription || frontmatter.subtitle || excerpt(content)}
        image={frontmatter.image}
        imageAlt={frontmatter.imageAlt}
        type="article"
        publishedTime={frontmatter.date}
        modifiedTime={frontmatter.updated || frontmatter.date}
      />
      <JsonLd data={buildBreadcrumbJsonLd(breadcrumbItems, lang)} />
      {frontmatter.qa && <JsonLd data={buildFaqJsonLd(frontmatter.qa)} />}
      {/* `embeddedFaq` is for prose articles that contain a FAQ section
       * inline in the markdown body (unlike `qa`, it does NOT replace the
       * body — it only adds FAQPage JSON-LD alongside the Article schema).
       * Keep the questions/answers here in sync with the matching H2/H3
       * block in the article body so structured data matches on-page content. */}
      {frontmatter.embeddedFaq && <JsonLd data={buildFaqJsonLd(frontmatter.embeddedFaq)} />}
      {frontmatter.terms && <JsonLd data={buildDefinedTermSetJsonLd(frontmatter.terms, frontmatter.title)} />}
      {/* Article sits alongside FAQPage/DefinedTermSet rather than instead
       * of them: those two say what the page's content IS, Article carries
       * the authorship and publish/update dates every knowledge page here
       * shows on screen anyway (§ T-15). */}
      <JsonLd
        data={buildArticleJsonLd({
          title: frontmatter.title,
          url: `https://talesandlegends.org/${lang}${path}`,
          lang,
          description: frontmatter.metaDescription || frontmatter.subtitle || excerpt(content),
          image: frontmatter.image,
          author: frontmatter.author,
          datePublished: frontmatter.date,
          dateModified: frontmatter.updated || frontmatter.date,
        })}
      />

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "40px", paddingTop: "32px", paddingBottom: "96px" }}>
          <Breadcrumbs items={breadcrumbItems} lang={lang} />

          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: "12px", color: "var(--color-ink)", background: "var(--color-gold)", padding: "5px 12px", borderRadius: "999px" }}>
                {frontmatter.topic}
              </span>
              <span style={{ fontSize: "13px", color: "var(--color-cream-faint)" }}>
                {t("larp.readingTime", { minutes: estimateReadingTime(getReadableText(page)) })}
              </span>
              {frontmatter.date && (
                <span style={{ fontSize: "13px", color: "var(--color-cream-faint)" }}>
                  {t("common.publishedOn", { date: formatDate(frontmatter.date, lang) }) +
                    (frontmatter.updated && frontmatter.updated !== frontmatter.date
                      ? ` · ${t("common.updatedOn", { date: formatDate(frontmatter.updated, lang) })}`
                      : "")}
                </span>
              )}
            </div>
            <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "46px", lineHeight: 1.08, color: "var(--color-cream)", margin: 0 }}>
              {frontmatter.h1 || frontmatter.title}
            </h1>
            {frontmatter.subtitle && (
              <p style={{ fontSize: "19px", lineHeight: 1.65, color: "var(--color-cream-soft)", margin: 0 }}>{frontmatter.subtitle}</p>
            )}
          </div>

          <ResponsiveImage
            src={frontmatter.image}
            alt={frontmatter.imageAlt || frontmatter.title}
            priority
            variant="dark"
            hideOnMissing
            style={{ width: "100%", height: "380px", borderRadius: "14px", objectFit: "cover", display: "block" }}
          />

          <div className="tl-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 250px", gap: "56px", alignItems: "start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              {frontmatter.qa && <FaqAccordion categories={groupFaqByCategory(frontmatter.qa)} />}

              {frontmatter.terms && (
                <dl style={{ display: "flex", flexDirection: "column", gap: "16px", margin: 0 }}>
                  {frontmatter.terms.map(({ term, definition }) => (
                    <div key={term} style={{ display: "flex", flexDirection: "column", gap: "4px", borderTop: "1px solid rgba(254,250,239,0.12)", paddingTop: "12px" }}>
                      <dt style={{ fontFamily: "var(--font-serif)", fontSize: "18px", color: "var(--color-cream)" }}>{term}</dt>
                      <dd style={{ fontSize: "15px", lineHeight: 1.6, color: "var(--color-cream-soft)", margin: 0 }}>{definition}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {!frontmatter.qa && !frontmatter.terms && (
                <div style={{ fontSize: "18px", lineHeight: 1.8, color: "var(--color-cream-soft)" }}>
                  <ReactMarkdown components={{ h2: ArticleH2, img: ArticleImage, blockquote: ArticleBlockquote }}>{content}</ReactMarkdown>
                </div>
              )}

              {/* Moved out of the sticky sidebar: there it sat above a
                  newsletter form tall enough to push the whole column past
                  the viewport, so reaching either meant scrolling to the
                  very bottom. Sharing is also something you decide once
                  you have read the thing, which is here. */}
              <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", borderTop: "1px solid rgba(254,250,239,0.12)", paddingTop: "24px" }}>
                <span style={{ fontSize: "11px", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-cream-faint)" }}>
                  {t("shareLinks.label")}
                </span>
                <ShareLinks shortUrl={frontmatter.shortUrl} title={frontmatter.title} showLabel={false} />
              </div>

              {/* `embeddedFaq` renders automatically here — articles never
               * hand-write their own FAQ heading/questions in the markdown
               * body, so there is exactly one place (frontmatter) to edit. */}
              {frontmatter.embeddedFaq && (
                <div style={{ display: "flex", flexDirection: "column", gap: "20px", borderTop: "1px solid rgba(254,250,239,0.12)", paddingTop: "32px" }}>
                  <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", color: "var(--color-cream)", margin: 0 }}>
                    {t("info.faqTitle")}
                  </h2>
                  <FaqAccordion items={frontmatter.embeddedFaq} />
                </div>
              )}

              {frontmatter.author && AUTHORS[frontmatter.author] && (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", borderTop: "1px solid rgba(254,250,239,0.12)", paddingTop: "24px" }}>
                  <span style={{ fontSize: "11px", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-cream-faint)" }}>
                    {t("larp.aboutAuthor")}
                  </span>
                  <Link
                    to={AUTHORS[frontmatter.author].teamPath(lang)}
                    style={{ fontFamily: "var(--font-serif)", fontSize: "18px", color: "var(--color-cream)" }}
                  >
                    {frontmatter.author}
                  </Link>
                  <p style={{ fontSize: "15px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0 }}>
                    {AUTHORS[frontmatter.author].bio[lang]}
                  </p>
                  {AUTHORS[frontmatter.author].sameAs?.length > 0 && (
                    <div style={{ display: "flex", gap: "16px" }}>
                      {AUTHORS[frontmatter.author].sameAs.map((href) => (
                        <a key={href} href={href} target="_blank" rel="noopener noreferrer" style={{ fontSize: "13px", color: "var(--color-gold)" }}>
                          {new URL(href).hostname.replace(/^www\./, "")}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "32px", flexWrap: "wrap", background: "var(--color-cream)", borderRadius: "14px", padding: "32px 36px", marginTop: "24px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: "1 1 320px" }}>
                  <span style={{ fontFamily: "var(--font-serif)", fontSize: "24px", lineHeight: 1.2, color: "var(--color-ink)" }}>
                    {t("larp.eventsCtaTitle")}
                  </span>
                  <span style={{ fontSize: "15px", lineHeight: 1.65, color: "rgba(10,9,7,0.68)" }}>{t("larp.eventsCtaBody")}</span>
                </div>
                <Link to={`/${lang}/events`} className="tl-pill tl-pill-dark" style={{ padding: "15px 30px", flex: "none" }}>
                  {t("larp.eventsCtaButton")}
                </Link>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "28px", position: "sticky", top: "96px" }}>
              {headings.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <span style={{ fontSize: "11px", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-cream-faint)" }}>
                    {t("larp.tableOfContents")}
                  </span>
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {headings.map((heading) => (
                      <a
                        key={heading.id}
                        href={`#${heading.id}`}
                        style={{ fontSize: "14px", color: "var(--color-cream-soft)", borderLeft: "2px solid rgba(254,250,239,0.14)", paddingLeft: "12px" }}
                      >
                        {heading.text}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: "10px", borderTop: headings.length > 0 ? "1px solid rgba(254,250,239,0.12)" : "none", paddingTop: headings.length > 0 ? "20px" : 0 }}>
                <span style={{ fontSize: "11px", letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-cream-faint)" }}>
                  {t("nav.newsletter")}
                </span>
                <p style={{ fontSize: "16px", lineHeight: 1.6, color: "var(--color-cream-soft)", margin: 0 }}>{t("home.newsletterBody")}</p>
                <div className="tl-card-light" style={{ padding: "16px" }}>
                  <NewsletterForm lang={lang} source="LARPDetails" variant="steps" />
                </div>
              </div>
            </div>
          </div>

          {relatedPages.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px", borderTop: "1px solid rgba(254,250,239,0.12)", paddingTop: "40px" }}>
              <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", color: "var(--color-cream)", margin: 0 }}>
                {t("larp.relatedPages")}
              </h2>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: "20px" }}>
                {relatedPages.map((related) => (
                  <Link
                    key={related.frontmatter.id}
                    to={`/${lang}/${LARP_PATH[lang]}/${related.frontmatter.slug}`}
                    className="tl-card tl-hover-raise"
                    style={{ display: "flex", flexDirection: "column", gap: "12px" }}
                  >
                    <span className="tl-eyebrow" style={{ fontSize: "12px" }}>
                      {`${related.frontmatter.topic} · ${t("larp.readingTime", { minutes: estimateReadingTime(getReadableText(related)) })}`}
                    </span>
                    <span style={{ fontFamily: "var(--font-serif)", fontSize: "20px", lineHeight: 1.25, color: "var(--color-cream)" }}>
                      {related.frontmatter.title}
                    </span>
                    <span style={{ fontSize: "13px", color: "var(--color-cream)", marginTop: "4px" }}>{t("larp.readMore")}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
